import React, { useEffect, useMemo, useRef, useState } from 'react';

import styled, { DefaultTheme, withTheme } from 'styled-components';
import { useForm } from 'react-hook-form';
import LinkIcon from 'remixicon-react/LinkIcon';
import MagicLineIcon from 'remixicon-react/MagicLineIcon';
import useModal from '../../services/hooks/useModal';
import { Modal } from '../../components/atoms/Modal/Modal';
import MagicPaylinkModal from '../../components/molecules/Paylink/MagicPaylinkModal';

import NotImplemented from '../../components/molecules/NotImplemented';
import CreatePaylinkForm from '../../components/molecules/Paylink/CreatePaylinkForm';
import { PaylinkFormRow } from '../../types/paylink/PaylinkFormRow';
import { ParsedCustomerInfo } from '../../types/paylink/ParsedCustomerInfo';
import { useAppState } from '../../store/appstate';
import { ConnectedStore } from '../../types/ConnectedStore';
import { CheckoutState, clearCheckoutInit } from '../../store/checkout/checkoutSlice';
import ModalContainer from '../../components/atoms/Modal/ModalContainer';
import FooterButtonContainer from '../../components/atoms/Modal/FooterButtonContainer';
import Button from '../../components/atoms/Button/Button';
import Spinner from '../../components/atoms/Spinner/Spinner';
import FormRow from '../../components/atoms/Form/FormRow';
import GridInput from '../../components/atoms/Input/GridInput';
import StoreSelect from '../../components/molecules/StoreSelect';
import Money from '../../components/atoms/Money/Money';
import CustomErrorMessage from '../../components/atoms/Message/Error/CustomErrorMessage';
import { ErrorType } from '../../types/response/ErrorCodes';
import ErrorMessage from '../../components/atoms/Message/Error/ErrorMessage';
import { useAppDispatch } from '../../store';
import EmptyState from '../../components/atoms/Information/EmptyState';
import { hasPermissions } from '../../services/helpers/hasPermissions';
import Radio from '../../components/atoms/Input/Radio';
import B2bPurchaseForm from '../../components/molecules/Paylink/B2bPurchaseForm';
import Flex from '../../components/atoms/Box/Flex';
import { InvoiceSendTypeToNumber } from '../../types/InvoiceSendType';
import CheckoutType from '../../types/CheckoutType';
import { CheckoutInitRows } from '../../types/requests/CheckoutInitRequest';
import CustomerSalesType from '../../types/customer/CustomerSalesType';
import { countrySpecificPhoneNumberValidationConfig } from '../../types/ValidationTypes';
import { PermissionOperation } from '../../types/PermissionOperation';
import PermissionType from '../../types/PermissionType';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultVatPerCountry } from '../../services/helpers/commonVat';
import MessageContainer from '../../components/atoms/Message/MessageContainer';
import { usePaylinksStore } from '../../store/checkout/paylinksStore';
import Logger from '../../services/logging/logger';
import { useEffectOnlyUpdateOnChanges } from '../../services/hooks/useEffectOnlyUpdateOnChanges';
import { countries } from '../../services/helpers/country';
import SelectInput from '../../components/atoms/Input/SelectInput';
import CheckBox from '../../components/atoms/Checkbox/Checkbox';
import AutoHeightTransition from '../../components/atoms/Transition/AutoHeightTransition';
import { useIsFlagEnabledForOrg } from '../../services/helpers/isFlagEnabled';

export enum CaptureStrategy {
    Uncaptured = 'Uncaptured',
    Autocaptured = 'Autocaptured',
}
interface CreatePurchaseProps {
    theme: DefaultTheme;
    onHide: () => void;
    type: CheckoutType;
    typeFriendlyName: string;
    onSendForm: (
        formData: PurchaseInputs,
        store: ConnectedStore,
        items: CheckoutInitRows[],
        type: CheckoutType
    ) => void;
    onCompleted: (setState: () => void) => void;
    articleNumber?: string | null;
    description?: string | null;
    quantity?: number | null;
    unitPrice?: number | null;
    vatRate?: number | null;
    initialCountryCode?: string | null;
    lock?: string[] | null;
    deliveryMethod?: string | null;
}

export type PurchaseInputs = {
    orderReference: string;
    email: string;
    phone: string;
    prefill: boolean;
    captureStrategy: CaptureStrategy;
    purchase: {
        invoiceAs: number;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        organisationNumber: string;
        reference: string;
        costCenter: string;
        companyName: string;
        sameAsDelivery: boolean;
        peppolId: string;
        delivery: {
            address: string;
            address2: string;
            coAddress: string;
            city: string;
            postalCode: string;
        };
        invoice: {
            address: string;
            address2: string;
            coAddress: string;
            city: string;
            postalCode: string;
        };
    };
};

const CreatePurchase: React.FC<CreatePurchaseProps> = ({
    theme,
    onHide,
    type,
    typeFriendlyName,
    onSendForm,
    onCompleted,
    articleNumber,
    description,
    unitPrice,
    vatRate,
    quantity,
    initialCountryCode,
    lock,
    deliveryMethod,
}: CreatePurchaseProps) => {
    const startTimeRef = useRef<number | null>(null);

    useEffect(() => {
        startTimeRef.current = Date.now();
    }, []);

    const logCreatePurchaseTiming = (data: PurchaseInputs, customerType: string | undefined) => {
        const start = startTimeRef.current ?? Date.now();
        const elapsedMs = Date.now() - start;
        const elapsedSeconds = Math.round(elapsedMs / 1000);
        const emailProvided = !!data.email;
        const phoneProvided = !!data.phone;
        const firstNameProvided = !!data.purchase?.firstName;
        const lastNameProvided = !!data.purchase?.lastName;
        const organizationNumberProvided = !!data.purchase?.organisationNumber;
        Logger.information(
            'Time spent creating purchase form {Type}, CustomerType: {CustomerType}. EmailProvided: {EmailProvided}, PhoneProvided: {PhoneProvided}, FirstNameProvided: {FirstNameProvided}, LastNameProvided: {LastNameProvided}, OrganizationNumberProvided: {OrganizationNumberProvided}',
            {
                Type: typeFriendlyName,
                CustomerType: customerType,
                Score: elapsedSeconds,
                EmailProvided: emailProvided,
                PhoneProvided: phoneProvided,
                FirstNameProvided: firstNameProvided,
                LastNameProvided: lastNameProvided,
                OrganizationNumberProvided: organizationNumberProvided,
            }
        );
    };

    const SelectStoreWrapper = styled.div`
        width: 20rem;
        align-self: center;
        margin-left: auto;
        margin-bottom: 3rem;
    `;

    const MoneyRow = styled(Flex)`
        margin-bottom: 3rem;
    `;

    const IndentPrefillContent = styled.div`
        margin-left: 3rem;
    `;

    const Tooltip = styled.div`
        background-color: ${props => props.theme.colors.accent};
        padding: 2rem;
    `;

    const RadioContainer = styled(Flex)`
        padding: 1rem 2rem;
        margin: 0.5rem 0;
        flex: 1;
    `;

    const options = Object.entries(countries).map(([value, { name, currency }]) => ({
        label: `${name} (${currency})`,
        value,
    }));

    const formRef = useRef<HTMLFormElement>(null);
    const isB2BPurchase =
        type === CheckoutType.SERVICE_INVOICE_PURCHASE ||
        type === CheckoutType.NO_FRAUD_PURCHASE ||
        type === CheckoutType.ADVANCE_INVOICE;

    const dispatch = useAppDispatch();
    const { setPhoneNumber } = usePaylinksStore();
    const [validate, setValidate] = useState(false);

    const { prefilledFromExistingPurchase, setPrefilledFromExistingPurchase } = usePaylinksStore();

    const getOperationBasedOnType = () => {
        switch (type) {
            case CheckoutType.NO_FRAUD_PURCHASE:
                return PermissionOperation.write_fraud_risk_invoice;

            case CheckoutType.SERVICE_INVOICE_PURCHASE:
                return PermissionOperation.write_service_invoice;

            case CheckoutType.ADVANCE_INVOICE:
                return PermissionOperation.write_advance_invoice;

            default:
                return PermissionOperation.write;
        }
    };

    const permissions = useAppState(s => s.session.user?.permissions) ?? [];

    const storesFilteredByPermission = (
        useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? []
    ).filter(x =>
        hasPermissions(PermissionType.checkoutDetails, getOperationBasedOnType(), permissions, x.id)
    );

    const countryCodes = storesFilteredByPermission.map(x => {
        if (x.isCheckout) return x.countryCode;
        return false;
    });

    const isOrgInDisableInStoreList = useIsFlagEnabledForOrg(
        'mx-a9056f7c-d9e5-474f-8b98-3e5df0d6bca1'
    );

    const lockDeliveryMethod =
        isOrgInDisableInStoreList || (lock ? lock.includes('deliveryMethod') : false);

    const countryCodeOptions = options.filter(x => countryCodes.includes(x && x.value)) ?? [];
    const [countryCode, setCountryCode] = useState<string>(
        prefilledFromExistingPurchase?.countryCode ??
            (countryCodeOptions.length !== 0 ? countryCodeOptions[0].value : 'SE')
    );

    useEffect(() => {
        if (initialCountryCode) setCountryCode(initialCountryCode);
    }, []);

    const prefilledArticle = {
        rowId: uuidv4(),
        articleNumber: articleNumber,
        description: description,
        unitPrice: unitPrice,
        vatRate: vatRate ?? getDefaultVatPerCountry(initialCountryCode ?? countryCode ?? 'SE'),
        quantity: quantity ?? 1,
    };

    const emptyRow = {
        rowId: uuidv4(),
        vatRate: getDefaultVatPerCountry(initialCountryCode ?? countryCode ?? 'SE'),
        quantity: 1,
    };

    const [formValues, setFormValues] = useState<PaylinkFormRow[]>([]);

    useEffect(() => {
        setFormValues(
            prefilledFromExistingPurchase
                ? [...prefilledFromExistingPurchase.articles, emptyRow as PaylinkFormRow]
                : articleNumber != null && description != null && unitPrice != null
                ? [prefilledArticle as PaylinkFormRow, emptyRow as PaylinkFormRow]
                : [prefilledArticle as PaylinkFormRow]
        );
    }, [prefilledFromExistingPurchase, articleNumber, description, unitPrice]);

    useEffectOnlyUpdateOnChanges(() => {
        const formValuesUpdatedVAT = formValues.map(row => ({
            ...row,
            vatRate: getDefaultVatPerCountry(countryCode),
        }));
        setFormValues([...formValuesUpdatedVAT]);
    }, [countryCode]);

    const filterStore = (store: ConnectedStore) => {
        if (isB2BPurchase)
            return (
                store.countryCode === countryCode &&
                store.customerSalesType === CustomerSalesType.B2B &&
                store.active
            );

        return store.countryCode === countryCode && store.isCheckout && store.active;
    };

    const filteredStoresFromChosenCurrency = useMemo(
        () => storesFilteredByPermission.filter(x => filterStore(x)) ?? [],
        [countryCode]
    );

    const { isInitializingCheckout, initCheckoutFailed } = useAppState<CheckoutState>(
        s => s.checkout
    );

    const [step, setStep] = useState<number>(1);
    const [store, setStore] = useState<string | number>('');
    const [createIsClicked, setCreateIsClicked] = useState(false);
    const { isShown: showAiImport, toggle: toggleAiImport } = useModal();

    const handleAiImport = (rows: PaylinkFormRow[], customer?: ParsedCustomerInfo) => {
        const rowsWithVat = rows.map(row => ({
            ...row,
            vatRate: getDefaultVatPerCountry(countryCode),
        }));
        setFormValues([
            ...rowsWithVat,
            {
                rowId: uuidv4(),
                vatRate: getDefaultVatPerCountry(countryCode),
                quantity: 1,
            } as PaylinkFormRow,
        ]);

        if (customer) {
            if (customer.email) setValue('email', customer.email);
            if (customer.phoneNumber) setValue('phone', customer.phoneNumber);
            if (customer.firstName) setValue('purchase.firstName', customer.firstName);
            if (customer.lastName) setValue('purchase.lastName', customer.lastName);
            if (customer.organisationNumber)
                setValue('purchase.organisationNumber', customer.organisationNumber);
            if (customer.companyName) setValue('purchase.companyName', customer.companyName);
            if (customer.address) setValue('purchase.delivery.address', customer.address);
            if (customer.postalCode) setValue('purchase.delivery.postalCode', customer.postalCode);
            if (customer.city) setValue('purchase.delivery.city', customer.city);
            setValue('prefill', true);
        }
    };

    const isProcessingPurchase = (isInitializingCheckout || createIsClicked) && !initCheckoutFailed;

    const initialCaptureStrategy =
        deliveryMethod === 'In-store' && !isOrgInDisableInStoreList
            ? CaptureStrategy.Autocaptured
            : CaptureStrategy.Uncaptured;

    const form = useForm<PurchaseInputs>({
            mode: 'onSubmit',
            defaultValues: {
                captureStrategy: initialCaptureStrategy,
                prefill: prefilledFromExistingPurchase ? true : false,
                purchase: {
                    invoiceAs: InvoiceSendTypeToNumber.EMAIL,
                    sameAsDelivery: prefilledFromExistingPurchase ? false : true,
                },
            },
            shouldUseNativeValidation: false,
        }),
        {
            register,
            handleSubmit,
            getValues,
            watch,
            setValue,
            formState: { errors },
        } = form;

    const watchCaptureStrategy = watch('captureStrategy', initialCaptureStrategy);
    const watchPrefill = watch('prefill', false);
    const watchPhone = watch('phone');

    const captureStrategy = register('captureStrategy');
    const orderReference = register('orderReference');
    const email = register('email');
    const phone = register(
        'phone',
        countrySpecificPhoneNumberValidationConfig(countryCode ?? 'SE')
    );
    const prefill = register('prefill');
    const orgNr = register('purchase.organisationNumber');
    const firstName = register('purchase.firstName');
    const lastName = register('purchase.lastName');

    useEffect(() => {
        setPhoneNumber(watchPhone);
    }, [watchPhone]);

    const chosenStore = filteredStoresFromChosenCurrency.find(x => x.id === store);

    const orderTotal =
        formValues
            .filter(x => Object.keys(x).length > 0 && x.quantity && x.unitPrice)
            .reduce((a, b) => a + b.quantity * b.unitPrice, 0) ?? 0;

    const onClickNext = () => {
        if (
            formRef.current &&
            !formRef.current?.reportValidity() &&
            !formRef.current?.checkValidity()
        )
            return;

        setValidate(true);

        setStep(step + 1);
        setValidate(false);
        dispatch(clearCheckoutInit());
    };

    const onClickBack = () => {
        setStep(step - 1);
        setCreateIsClicked(false);
    };

    const onHideCreatePurchaseModal = () => {
        setCreateIsClicked(false);
        setPrefilledFromExistingPurchase(undefined);
        dispatch(clearCheckoutInit());
        onHide();
    };

    const onSubmit = (data: PurchaseInputs) => {
        if (!chosenStore) return;
        logCreatePurchaseTiming(data, chosenStore.customerSalesType);

        const cleanedRows = formValues
            .filter(row => row.articleNumber)
            .map(x => {
                return {
                    id: x.articleNumber,
                    description: x.description,
                    quantity: x.quantity,
                    unitPrice: x.unitPrice,
                    vat: x.vatRate,
                    requiresElectronicId: true,
                };
            });

        onSendForm(data, chosenStore, cleanedRows, type);
        setCreateIsClicked(true);
    };

    const renderErrorMessage = (code: number) => {
        switch (code) {
            case 401:
                return (
                    <CustomErrorMessage errorHeader={getCreateErrorText()}>
                        You do not have permission to create purchases on this store.
                    </CustomErrorMessage>
                );
            default:
                return (
                    <ErrorMessage errorHeader={getCreateErrorText()} error={initCheckoutFailed} />
                );
        }
    };

    const getCreateErrorText = () => {
        switch (type) {
            case CheckoutType.SERVICE_INVOICE_PURCHASE:
                return ErrorType.CreateServiceInvoice;
            case CheckoutType.NO_FRAUD_PURCHASE:
                return ErrorType.CreateNoFraudInvoice;
            case CheckoutType.ADVANCE_INVOICE:
                return ErrorType.CreateAdvanceInvoice;
            case CheckoutType.PAYLINK:
                return ErrorType.CreatePaylink;
            default:
                return ErrorType.CreateInvoice;
        }
    };

    useEffect(() => {
        onCompleted(onHideCreatePurchaseModal);
    }, [isInitializingCheckout]);

    useEffect(() => {
        setStore(
            prefilledFromExistingPurchase?.storeId ??
                (filteredStoresFromChosenCurrency.length === 1
                    ? filteredStoresFromChosenCurrency[0].id
                    : '')
        );
    }, [filteredStoresFromChosenCurrency]);

    const renderStep1 = () => {
        return (
            <>
                <Flex
                    row
                    gap={theme.layout.gap.medium}
                    justifyContent={'space-between'}
                    alignItems="center"
                >
                    <h3>Add articles</h3>
                    <AiImportButton
                        type="button"
                        onClick={toggleAiImport}
                        data-testid="ai-import-button"
                    >
                        <MagicLineIcon size={16} color={theme.colors.accent3} />
                        AI Import
                    </AiImportButton>
                </Flex>
                <Modal medium elevated isShown={showAiImport} hide={toggleAiImport}>
                    <MagicPaylinkModal hide={toggleAiImport} onImport={handleAiImport} />
                </Modal>
                <Flex row gap={theme.layout.gap.medium} justifyContent={'space-between'}>
                    {type === CheckoutType.ADVANCE_INVOICE && (
                        <OuterMessageContainer>
                            <MessageContainer type="Warning" header={'Before you ship anything...'}>
                                For advance invoice Walley doesn't guarantee the payment, so you
                                must ensure the customer has paid the invoice before shipping any
                                orders.
                            </MessageContainer>
                        </OuterMessageContainer>
                    )}

                    <SelectStoreWrapper>
                        <SelectInput
                            id="label"
                            dataTestId="country-selector"
                            placeholder="Country"
                            onChange={setCountryCode}
                            defaultValue="No country chosen"
                            value={countryCode}
                            options={countryCodeOptions}
                        />
                    </SelectStoreWrapper>
                </Flex>

                <form ref={formRef}>
                    <CreatePaylinkForm
                        countryCode={countryCode}
                        validate={validate}
                        setFormValues={setFormValues}
                        formValues={formValues}
                        onSend={onClickNext}
                        setValidate={setValidate}
                        lock={lock}
                    />
                </form>
            </>
        );
    };

    const renderStep2 = () => {
        return (
            <ConfirmContainer column gap={theme.layout.gap.small}>
                <ConfirmContent>
                    {step === 2 && (
                        <MoneyRow row gap={theme.layout.gap.xLarge} alignItems="center">
                            <div>Order total</div>
                            <MoneyRowValue>
                                <Money>{orderTotal}</Money> {countries[countryCode].currency}
                            </MoneyRowValue>
                        </MoneyRow>
                    )}

                    {step === 2 && (
                        <StoreSelect
                            dataTestId="select-store"
                            placeholder="Select store"
                            onChange={setStore}
                            value={store}
                            availableStores={filteredStoresFromChosenCurrency}
                        />
                    )}
                </ConfirmContent>

                <ContentRow column gap={theme.layout.gap.medium}>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        data-testid="create-paylink-form"
                        id="create-paylink-form"
                    >
                        <ConfirmContent>
                            {step === 2 && (
                                <>
                                    {type === CheckoutType.PAYLINK ? (
                                        <>
                                            <FormRow first>
                                                <GridInput
                                                    type="text"
                                                    id="orderReference"
                                                    placeholderRaised={
                                                        !!getValues('orderReference')
                                                    }
                                                    placeholder="Merchant order reference (optional)"
                                                    name={orderReference.name}
                                                    onChange={e => orderReference.onChange(e)}
                                                    setRef={orderReference.ref}
                                                    defaultValue={
                                                        prefilledFromExistingPurchase?.orderReference ??
                                                        undefined
                                                    }
                                                />
                                            </FormRow>
                                            <FormRow last>
                                                <GridInput
                                                    type="text"
                                                    id="phone"
                                                    placeholderRaised={!!getValues('phone')}
                                                    placeholder="Buyer mobile phone number (optional)"
                                                    name={phone.name}
                                                    onChange={e => phone.onChange(e)}
                                                    setRef={phone.ref}
                                                    defaultValue={
                                                        prefilledFromExistingPurchase?.phoneNumber ??
                                                        undefined
                                                    }
                                                    validationError={!!errors.phone}
                                                />
                                            </FormRow>
                                        </>
                                    ) : (
                                        <FormRow first last>
                                            <GridInput
                                                type="text"
                                                id="orderReference"
                                                placeholderRaised={!!getValues('orderReference')}
                                                placeholder="Merchant order reference (optional)"
                                                name={orderReference.name}
                                                onChange={e => orderReference.onChange(e)}
                                                setRef={orderReference.ref}
                                                defaultValue={
                                                    prefilledFromExistingPurchase?.orderReference ??
                                                    undefined
                                                }
                                            />
                                        </FormRow>
                                    )}
                                    {chosenStore && type === CheckoutType.PAYLINK && (
                                        <>
                                            <br />
                                            <Flex
                                                row
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <CheckBox
                                                    id="prefill"
                                                    name={prefill.name}
                                                    onChange={e => prefill.onChange(e)}
                                                    setRef={prefill.ref}
                                                    onBlur={prefill.onBlur}
                                                >
                                                    Prefill customer information (all fields
                                                    optional)
                                                </CheckBox>
                                            </Flex>
                                            <AutoHeightTransition
                                                expand={!!watchPrefill}
                                                transitionTime="0.3s"
                                            >
                                                <IndentPrefillContent>
                                                    {chosenStore.customerSalesType ===
                                                    CustomerSalesType.B2B ? (
                                                        <>
                                                            <h3>Company details</h3>
                                                            <FormRow first last>
                                                                <GridInput
                                                                    type="text"
                                                                    id="organisationNumber"
                                                                    placeholderRaised={
                                                                        !!getValues(
                                                                            'purchase.organisationNumber'
                                                                        )
                                                                    }
                                                                    placeholder="Organization number"
                                                                    name={orgNr.name}
                                                                    onChange={e =>
                                                                        orgNr.onChange(e)
                                                                    }
                                                                    setRef={orgNr.ref}
                                                                    defaultValue={
                                                                        prefilledFromExistingPurchase?.registrationNumber ??
                                                                        undefined
                                                                    }
                                                                />
                                                            </FormRow>
                                                            <FormItemRow>
                                                                <h3>Buyer details</h3>
                                                                <FormRow first>
                                                                    <GridInput
                                                                        type="text"
                                                                        id="firstName"
                                                                        placeholderRaised={
                                                                            !!getValues(
                                                                                'purchase.firstName'
                                                                            )
                                                                        }
                                                                        placeholder="First name"
                                                                        name={firstName.name}
                                                                        onChange={e =>
                                                                            firstName.onChange(e)
                                                                        }
                                                                        setRef={firstName.ref}
                                                                        defaultValue={
                                                                            prefilledFromExistingPurchase?.firstName ??
                                                                            undefined
                                                                        }
                                                                    />
                                                                    <GridInput
                                                                        type="text"
                                                                        id="lastName"
                                                                        placeholderRaised={
                                                                            !!getValues(
                                                                                'purchase.lastName'
                                                                            )
                                                                        }
                                                                        placeholder="Family name"
                                                                        name={lastName.name}
                                                                        onChange={e =>
                                                                            lastName.onChange(e)
                                                                        }
                                                                        setRef={lastName.ref}
                                                                        defaultValue={
                                                                            prefilledFromExistingPurchase?.lastName ??
                                                                            undefined
                                                                        }
                                                                    />
                                                                </FormRow>
                                                            </FormItemRow>
                                                            <FormRow last>
                                                                <GridInput
                                                                    type="email"
                                                                    id="email"
                                                                    placeholderRaised={
                                                                        !!getValues('email')
                                                                    }
                                                                    placeholder="Email"
                                                                    name={email.name}
                                                                    onChange={e =>
                                                                        email.onChange(e)
                                                                    }
                                                                    setRef={email.ref}
                                                                    defaultValue={
                                                                        prefilledFromExistingPurchase?.email ??
                                                                        undefined
                                                                    }
                                                                />
                                                            </FormRow>
                                                        </>
                                                    ) : (
                                                        <FormRow first last>
                                                            <GridInput
                                                                type="email"
                                                                id="email"
                                                                placeholderRaised={
                                                                    !!getValues('email')
                                                                }
                                                                placeholder="Email"
                                                                name={email.name}
                                                                onChange={e => email.onChange(e)}
                                                                setRef={email.ref}
                                                                defaultValue={
                                                                    prefilledFromExistingPurchase?.email ??
                                                                    undefined
                                                                }
                                                            />
                                                        </FormRow>
                                                    )}
                                                </IndentPrefillContent>
                                            </AutoHeightTransition>
                                            <br />
                                        </>
                                    )}

                                    <FormItemRow>
                                        <h3>Select sales delivery method</h3>

                                        <RadioContainer row gap={theme.layout.gap.xLarge}>
                                            {lockDeliveryMethod ? (
                                                <>
                                                    <Radio
                                                        id="captureStrategy"
                                                        disabled
                                                        name={captureStrategy.name}
                                                        value={initialCaptureStrategy}
                                                        setRef={captureStrategy.ref}
                                                    >
                                                        <label htmlFor="captureStrategy">
                                                            {initialCaptureStrategy ==
                                                            CaptureStrategy.Uncaptured
                                                                ? 'Remote'
                                                                : 'In-store'}
                                                        </label>
                                                    </Radio>
                                                </>
                                            ) : (
                                                <>
                                                    <Radio
                                                        id="captureStrategy-delivery"
                                                        name={captureStrategy.name}
                                                        value={CaptureStrategy.Uncaptured}
                                                        onChange={e => captureStrategy.onChange(e)}
                                                        setRef={captureStrategy.ref}
                                                    >
                                                        <label htmlFor="captureStrategy-delivery">
                                                            Remote
                                                        </label>
                                                    </Radio>
                                                    <Radio
                                                        id="captureStrategy-store"
                                                        name={captureStrategy.name}
                                                        value={CaptureStrategy.Autocaptured}
                                                        onChange={e => captureStrategy.onChange(e)}
                                                        setRef={captureStrategy.ref}
                                                    >
                                                        <label htmlFor="captureStrategy-store">
                                                            In-store
                                                        </label>
                                                    </Radio>
                                                </>
                                            )}
                                        </RadioContainer>
                                        <Tooltip>
                                            {watchCaptureStrategy == CaptureStrategy.Uncaptured && (
                                                <span>
                                                    Use this option when you sell items on distance,
                                                    for example over phone or email. You will need
                                                    to &#34;Activate&#34; the order when the items
                                                    are about to be shipped to the customer.
                                                </span>
                                            )}

                                            {watchCaptureStrategy ==
                                                CaptureStrategy.Autocaptured && (
                                                <span>
                                                    Use this option when the customer receives the
                                                    items directly, for example in your physical
                                                    store. The order will later get
                                                    &#34;Activated&#34; when the customer has
                                                    completed their purchase.
                                                </span>
                                            )}
                                        </Tooltip>
                                    </FormItemRow>
                                </>
                            )}
                        </ConfirmContent>
                        {step === 3 && isB2BPurchase && (
                            <B2bPurchaseForm form={form} countryCode={countryCode} />
                        )}
                    </form>
                </ContentRow>
            </ConfirmContainer>
        );
    };

    if (
        storesFilteredByPermission &&
        storesFilteredByPermission.filter(x => x.isCheckout).length === 0
    ) {
        return (
            <>
                <ModalContainer position="header">
                    <PaylinkTitle>New Purchase</PaylinkTitle>
                </ModalContainer>
                <ModalContainer position="content">
                    <EmptyState
                        title="There is no stores configured for Purchase"
                        icon={
                            <LinkIcon size={theme.icon.size.xlarge} color={theme.colors.accent3} />
                        }
                    >
                        If you want a purchase configured please contact help@walleypay.com
                    </EmptyState>
                </ModalContainer>
                <ModalContainer position="footer">
                    <FooterButtonContainer>
                        <Button onClick={onHideCreatePurchaseModal} large>
                            Close
                        </Button>
                    </FooterButtonContainer>
                </ModalContainer>
            </>
        );
    }

    return storesFilteredByPermission ? (
        <>
            <ModalContainer position="header">
                <PaylinkTitle>New purchase - {typeFriendlyName}</PaylinkTitle>
            </ModalContainer>

            <ModalContainer position="content">
                {step === 1 && renderStep1()}
                {(step === 2 || step === 3) && renderStep2()}
            </ModalContainer>

            {initCheckoutFailed && (
                <ModalContainer position="error">
                    {renderErrorMessage(initCheckoutFailed.status)}
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <div>
                    <FooterButtonContainer>
                        <Button
                            disabled={isInitializingCheckout}
                            onClick={onHideCreatePurchaseModal}
                            large
                        >
                            Cancel
                        </Button>
                        <Button disabled={step === 1} onClick={() => onClickBack()} large>
                            Back
                        </Button>

                        {((step === 2 && !isB2BPurchase) || step === 3) && (
                            <Button
                                data-testid="form-submit"
                                tabIndex={0}
                                form="create-paylink-form"
                                type="submit"
                                primary
                                large
                                disabled={isProcessingPurchase || store === ''}
                            >
                                {isProcessingPurchase ? (
                                    <Spinner color={theme.colors.light} size={8} loading />
                                ) : (
                                    <span>Create</span>
                                )}
                            </Button>
                        )}

                        {(step < 2 || (isB2BPurchase && step === 2)) && (
                            <Button
                                data-testid="form-next"
                                onClick={() => onClickNext()}
                                tabIndex={0}
                                primary
                                large
                                disabled={
                                    isInitializingCheckout ||
                                    formValues.length === 1 ||
                                    (step === 2 && store === '')
                                }
                            >
                                {isProcessingPurchase ? (
                                    <Spinner color={theme.colors.light} size={8} loading />
                                ) : (
                                    <span>Next</span>
                                )}
                            </Button>
                        )}
                    </FooterButtonContainer>
                </div>
            </ModalContainer>
        </>
    ) : (
        <NotImplemented />
    );
};

export default withTheme(CreatePurchase) as React.ComponentType<Omit<CreatePurchaseProps, 'theme'>>;

const ContentRow = styled(Flex)`
    margin-bottom: 2rem;
`;

const MoneyRowValue = styled.div`
    font-family: ${props => props.theme.text.font.bold};
    font-size: ${props => props.theme.text.size.xlarge};
    margin-bottom: -0.3rem;
`;

const ConfirmContainer = styled(Flex)`
    padding: 2rem;
`;

const ConfirmContent = styled.div`
    width: 50%;
`;

const OuterMessageContainer = styled.div`
    width: fit-content;
    height: fit-content;
`;

const PaylinkTitle = styled.a`
    font-family: ${props => props.theme.text.font.bold};
    margin-right: 2rem;
    font-size: ${props => props.theme.text.size.xlarge};
    line-height: 2.5rem;
`;

const FormItemRow = styled.div`
    margin-top: 2rem;
`;

const AiImportButton = styled.button`
    position: relative;
    overflow: visible;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: 0.1rem solid ${props => props.theme.colors.border};
    border-radius: 0.4rem;
    padding: 0.5rem 1.2rem;
    cursor: pointer;
    color: ${props => props.theme.colors.primary};
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.medium};
    white-space: nowrap;

    &:hover {
        background: ${props => props.theme.colors.subtle.regular};
    }
`;
